import { fromEvent, pipe, subject, subscribe, switchMap, takeUntil, tap } from 'fastrx';
import { AlphaStitchingType, ILogger } from '../../enums';
import { Video2dContext } from '../video-context';
import { VideoNode } from './base';
import kvStatManager, { KV_REPORT_KEY_VIDEO_POST_PROCESS } from '../kv-stat-manager';

interface InitStat {
  alphaStitchingType: AlphaStitchingType;
}

export class AlphaNode extends VideoNode<TexImageSource, Video2dContext> {
  initStat: InitStat = { alphaStitchingType: AlphaStitchingType.horizontal };
  private end = subject<void>();
  private minSize = 320;
  private maxSize = 1280;
  draggable = false;
  private startDragX = 0;
  private startDragY = 0;
  private left = 0;
  private top = 0;
  baseWidth = 320;
  baseRatio?: number;
  container?: HTMLElement;
  constructor(context: Video2dContext, logger: ILogger, draggable: boolean, private setContainer: () => void, initStat: InitStat) {
    super(context, {
      useDefaultProgram: true,
      useFbo: true,
      name: 'alpha',
      create2d: true,
      logger
    });
    this.initStat = initStat;
    this.draggable = draggable;
    this.bindDragEvents();
    // value 1 代表近透明通道渲染，11 代表也开启了拖拽
    kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_POST_PROCESS.ALPHA_RESULT, value: 1 });
    if (this.draggable) kvStatManager.addEnum({ key: KV_REPORT_KEY_VIDEO_POST_PROCESS.ALPHA_RESULT, value: 11 });
  }
  bindDragEvents() {
    const canvas = this.context._canvas;
    if (!canvas) return;
    if (this.draggable) {
      const endPipe = takeUntil(this.end);
      pipe(
        fromEvent(canvas, 'mousedown'),
        tap(this.startDrag.bind(this)),
        switchMap(() => pipe(fromEvent(window, 'mousemove'), takeUntil(fromEvent(window, 'mouseup')))),
        endPipe,
        subscribe(this.doDrag.bind(this))
      );
      pipe(fromEvent(canvas, 'dblclick'), endPipe, subscribe(this.resetPosition.bind(this)));
      pipe(fromEvent(canvas, 'wheel'), endPipe, subscribe(this.handleZoom.bind(this)));
      this.renderCanvas();
    } else {
      // 避免 draggable参数更新为 false 的时候，container 显示不完全
      if (!this.container) return;
      this.container.style.removeProperty('left');
      this.container.style.removeProperty('top');
      this.end.next();
    }
  }
  render(seq: number): boolean {
    if (this.input?.requestFrame(seq)) {
      this.useProgram();
      this.useBufferFrame();
      this.useInputTexture();
      this.draw();
      return true;
    }
    return false;
  }
  startDrag(e: MouseEvent) {
    e.preventDefault();
    if (e.button !== 0) return;
    this.startDragX = e.clientX - this.left;
    this.startDragY = e.clientY - this.top;
  }
  renderCanvas() {
    const { container } = this;
    if (!container) this.setContainer();
    if (!container || !this.baseRatio || !this.draggable) return;
    container.style.setProperty('width', `${this.baseWidth}px`);
    container.style.setProperty('height', `${this.baseWidth / this.baseRatio}px`);
    container.style.setProperty('position', 'fixed');
    container.style.setProperty('left', `${this.left}px`);
    container.style.setProperty('top', `${this.top}px`);
  }
  doDrag(e: MouseEvent) {
    e.preventDefault();
    this.left = e.clientX - this.startDragX;
    this.top = e.clientY - this.startDragY;
    this.renderCanvas();
  }
  handleZoom(e: WheelEvent) {
    e.preventDefault();
    const zoomFactor = 0.1;
    const delta = e.deltaY;
    const canvas = this.context._canvas;
    if (!canvas) return;
    if (!this.baseWidth) {
      this.baseWidth = canvas.offsetWidth;
    }
    if (delta < 0) {
      this.baseWidth = Math.min((this.baseWidth) * (1 + zoomFactor), this.maxSize);
    } else {
      this.baseWidth = Math.max((this.baseWidth) * (1 - zoomFactor), this.minSize);
    }
    this.renderCanvas();
  }
  resetPosition() {
    this.left = 0;
    this.top = 0;
    this.renderCanvas();
  }
  onRatioReset() {
    this.renderCanvas();
  }

  draw2d(image: TexImageSource | undefined, dx: number, dy: number, dw: number, dh: number): boolean {
    const { ctx2d } = this;
    const canvas = this.context._canvas;
    if (!ctx2d || !canvas) return false;
    const result1 = super.draw2d(image, dx, dy, dw, dh);
    const imageData = ctx2d.getImageData(0, 0, dw, dh);
    const { data } = imageData;
    let result2 = false;
    if (this.initStat.alphaStitchingType === AlphaStitchingType.horizontal) {
      const halfWidth = Math.floor(dw / 2);
      for (let y = 0; y < dh; y++) {
        for (let x = 0; x < halfWidth; x++) {
          const pixelIdx = (y * dw + x) * 4;
          const maskX = x + halfWidth;
          const maskPixelIdx = (y * dw + maskX) * 4;
          const maskR = data[maskPixelIdx];
          data[maskPixelIdx + 3] = 0;
          const isOpaque = (maskR >= 100);
          data[pixelIdx + 3] = isOpaque ? 255 : 0;
        }
      }
      result2 = super.draw2d(
        imageData,
        0, 0,
        0, 0,
        halfWidth, dh
      );
      canvas.width = halfWidth;
    } else if (this.initStat.alphaStitchingType === AlphaStitchingType.vertical) {
      const halfHeight = Math.floor(dh / 2);
      for (let y = 0; y < halfHeight; y++) {
        for (let x = 0; x < dw; x++) {
          const pixelIdx = (y * dw + x) * 4;
          const maskY = y + halfHeight;
          const maskPixelIdx = (maskY * dw + x) * 4;
          const maskR = data[maskPixelIdx];
          data[maskPixelIdx + 3] = 0;
          const isOpaque = (maskR >= 100);
          data[pixelIdx + 3] = isOpaque ? 255 : 0;
        }
      }
      result2 = super.draw2d(
        imageData,
        0, 0,
        0, 0,
        dw, halfHeight
      );
      canvas.height = halfHeight;
    }
    this.context.ctx?.clearRect(0, 0, dw, dh);
    return result1 && result2;
  }
  close(): void {
    this.baseRatio = undefined;
    this.end.next();
    this.end.complete();
  }
}
